import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import I18n from 'i18n-js';
import SoundEffectResultList from '@App/components/page/soundEffects/ResultList';
import SoundEffectFilters from '@App/components/page/soundEffects/SoundEffectFilters';
import Modal from '@App/components/layout/Modal';
import { default as SubscriptionIncentiveBanner } from '@App/components/model/subscription/IncentiveBanner';
import { closeFilterModal } from '@App/store/actions/navigation';
import {
  clearSoundEffectCategory,
  clearSoundEffectSearch,
  selectSoundEffectCategory,
  setSoundEffectTerm,
} from '@App/store/actions/soundEffect';
import { SIZE_LG } from '@App/service/MobileWatcher';
import { useAppSelector } from '@App/store';

/**
 * Sound effects page (/effets-sonores).
 *
 * Dedicated result page for Pro Sound Effects sound effects. Its criteria (text term + UCS category)
 * are held in the `soundEffect` store slice, because the search box itself is part of the fixed
 * header (see {@link SoundEffectSearchBar}) while the category column and the results are here.
 *
 * The page is never empty: with no term and no category, the query-less `/Search` lists the head of
 * the catalogue, so a visitor lands on sounds to play instead of an invitation to type.
 *
 * The category column is shown to every visitor, subscribed or not, exactly as the music library shows
 * its tag column to anyone: browsing the catalogue IS the shop window. A subscription changes what the
 * download button of a row does, not what may be explored. The layout mirrors that library too — the
 * column flush against the left edge of `main`, results beside it — rather than nesting a narrower one
 * inside the content.
 */
export default function SoundEffects() {
  const dispatch = useDispatch();
  const initialSearch = useAppSelector(
    state => (state.navigation as { query?: { search?: string } }).query?.search ?? '',
  );
  const { term, catId } = useAppSelector(state => state.soundEffect);
  // Same notion of "searching" as the results block below reports on: a term, a category, or both.
  const searching = term !== '' || Boolean(catId);

  // Sync the term with the navigation query: prefill when arriving from the global search with a
  // term, and clear when navigated back via a plain link (empty query) so a stale search does not
  // linger. Only re-runs on navigation, so typing between navigations is kept.
  useEffect(() => {
    dispatch(setSoundEffectTerm(initialSearch));
  }, [dispatch, initialSearch]);

  // Leaving the page must not keep criteria (nor a chip in a bar that no longer exists) around.
  useEffect(() => () => {
    dispatch(clearSoundEffectSearch());
  }, [dispatch]);

  return (
    <>
      <FilterColumn
        selectedCatId={catId}
        onSelect={(id, label) => dispatch(selectSoundEffectCategory(id, label))}
        onClear={() => dispatch(clearSoundEffectCategory())}
      />

      <div className="content">
        <div className="container-fluid sound-effects__page">
          <PageHeader searching={searching} />
          <SubscriptionIncentiveBanner />
          <SoundEffectResultList search={term} catId={catId} />
        </div>
      </div>
    </>
  );
}

/**
 * Header of the page: the H1, its BETA badge and the SEO paragraph.
 *
 * Separate from the results block on purpose. The title of that block is a live counter — it becomes
 * "N effets sonores trouvés" as soon as a search runs — so it cannot double as the page title, which
 * is why this header exists.
 *
 * It steps aside while a search is running, as asked in the recette: what it says announces the
 * catalogue, not a result, and it would only push the results down. The H1 stays in the document
 * though, merely hidden from sight — a page must not lose its first-level heading, and the address
 * search engines read is the one with no query, where the header is fully visible.
 */
function PageHeader({ searching }: { searching: boolean }) {
  return (
    <header className={`sound-effects__header${searching ? ' sound-effects__header--searching' : ''}`}>
      <h1 className={searching ? 'sr-only' : 'sound-effects__title'}>
        {I18n.t('soundEffects.page.title')}
        {!searching && <span className="sound-effects__badge">{I18n.t('soundEffects.page.badge')}</span>}
      </h1>
      {!searching && <p className="sound-effects__intro">{I18n.t('soundEffects.page.intro')}</p>}
    </header>
  );
}

interface FilterColumnProps {
  selectedCatId: string | null
  onSelect: (id: string, label: string) => void
  onClear: () => void
}

/**
 * The category column, positioned exactly as the music library's one: an `aside` at the left edge on
 * desktop, and on mobile — where there is no room for a permanent column — the same modal opened
 * from the search bar's filter button (shared `filterModalOpen` navigation state).
 */
function FilterColumn({ selectedCatId, onSelect, onClear }: FilterColumnProps) {
  const dispatch = useDispatch();
  const mobile = useAppSelector(state => (state.navigation as { size: number }).size < SIZE_LG);
  const modalOpen = useAppSelector(
    state => (state.navigation as { filterModalOpen: boolean }).filterModalOpen,
  );

  const filters = (
    <SoundEffectFilters selectedCatId={selectedCatId} onSelect={onSelect} onClear={onClear} />
  );

  if (mobile) {
    if (!modalOpen) {
      return null;
    }

    return (
      <Modal
        title={I18n.t('search.filters.title')}
        icon="filter"
        onClose={() => dispatch(closeFilterModal())}
      >
        <div className="filters">{filters}</div>
      </Modal>
    );
  }

  return <aside className="filters">{filters}</aside>;
}
